/**
 * Admin Menus Page
 * Monthly calendar view for managing menus with create/edit/publish/delete
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useUIStore } from '@/stores/uiStore';
import { MenuCalendar } from '@/components/admin/MenuCalendar';
import { MenuModal } from '@/components/admin/MenuModal';
import { DeleteMenuDialog } from '@/components/admin/DeleteMenuDialog';
import {
  fetchMenus,
  publishMenu,
  deleteMenu,
} from '@/lib/api/menus';
import {
  getMonthRange,
  toISODate,
  formatDate,
  nextMonth,
  prevMonth,
} from '@/lib/utils/dateHelpers';
import { ChevronLeft, ChevronRight, Plus, Send } from 'lucide-react';
import type { Menu } from '@/types/api';

export function Menus() {
  const { showToast } = useUIStore();

  // Current month for calendar view - default to next month for planning ahead
  const [currentMonth, setCurrentMonth] = useState<Date>(() => nextMonth(new Date()));

  // Data state
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const publishInFlight = useRef(false);

  // Modal state
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState<Menu | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Count draft menus
  const draftMenus = useMemo(
    () => menus.filter((m) => m.status === 'DRAFT'),
    [menus]
  );

  // Month display
  const monthDisplay = useMemo(
    () => formatDate(currentMonth, 'MMMM yyyy'),
    [currentMonth]
  );

  // Load menus for the current month
  const loadMenus = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = getMonthRange(currentMonth);
      const data = await fetchMenus(toISODate(start), toISODate(end));
      setMenus(data);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to load menus',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [currentMonth, showToast]);

  // Load on mount and month change
  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  // Navigate months
  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prevMonth(prev));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => nextMonth(prev));
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  // Handle date click in calendar
  const handleDateClick = (date: Date, menu?: Menu) => {
    if (menu) {
      // Edit existing menu
      setSelectedMenu(menu);
      setSelectedDate(null);
    } else {
      // Create new menu
      setSelectedMenu(null);
      setSelectedDate(date);
    }
    setMenuModalOpen(true);
  };

  // Publish all drafts
  const handlePublishAllDrafts = async () => {
    // Guard against duplicate submissions (double-clicks / retries) firing a
    // second round of publish calls before React re-renders the disabled button.
    if (draftMenus.length === 0 || publishInFlight.current) return;
    publishInFlight.current = true;

    try {
      setPublishing(true);
      // Publish all draft menus in parallel
      const results = await Promise.all(draftMenus.map((menu) => publishMenu(menu.id)));
      const autoRsvpTotal = results.reduce((sum, result) => sum + (result?.autoRsvpCount ?? 0), 0);
      const autoRsvpFailures = results.filter((result) => result?.autoRsvpFailed).length;
      const autoRsvpSuffix =
        autoRsvpTotal > 0 ? ` and auto-RSVP'd ${autoRsvpTotal} member(s)` : '';
      showToast(`Published ${draftMenus.length} menu(s) successfully${autoRsvpSuffix}`, 'success');
      // Publish itself succeeded, but the best-effort auto-RSVP step failed for
      // some menus — warn the admin so opted-in members aren't silently missed.
      if (autoRsvpFailures > 0) {
        showToast(
          `Automatic RSVP could not run for ${autoRsvpFailures} menu(s). Opted-in members may not have been RSVP'd — please check and retry.`,
          'error'
        );
      }
      await loadMenus();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to publish menus';
      if (message.toLowerCase().includes('cooking day')) {
        showToast('One or more draft menus are missing a valid cooking day. Edit the menu and try again.', 'error');
        return;
      }
      showToast(
        message,
        'error'
      );
    } finally {
      setPublishing(false);
      publishInFlight.current = false;
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!menuToDelete) return;

    try {
      setIsDeleting(true);
      await deleteMenu(menuToDelete.id);
      showToast('Menu deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setMenuToDelete(null);
      await loadMenus();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to delete menu',
        'error'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle modal success (create/edit)
  const handleModalSuccess = () => {
    loadMenus();
  };

  return (
    <div className="container mx-auto p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Menu Management</h1>
          <p className="text-muted-foreground">
            Create and manage daily menus
          </p>
        </div>
        <div className="flex gap-2">
          {draftMenus.length > 0 && (
            <Button
              variant="default"
              onClick={handlePublishAllDrafts}
              disabled={publishing}
            >
              <Send className="h-4 w-4 mr-2" />
              {publishing ? 'Publishing...' : `Publish All Drafts`}
              <Badge variant="secondary" className="ml-2 bg-white/20">
                {draftMenus.length}
              </Badge>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setSelectedMenu(null);
              setSelectedDate(new Date());
              setMenuModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Menu
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <Card className="p-4 mb-6">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevMonth}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold">{monthDisplay}</h2>
            <Button variant="outline" size="sm" onClick={handleToday}>
              Today
            </Button>
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Calendar Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <MenuCalendar
          month={currentMonth}
          menus={menus}
          onDateClick={handleDateClick}
        />
      )}

      {/* Menu Modal (Create/Edit) */}
      <MenuModal
        open={menuModalOpen}
        onOpenChange={setMenuModalOpen}
        menu={selectedMenu}
        defaultDate={selectedDate ?? undefined}
        onSuccess={handleModalSuccess}
        onDelete={(menu) => {
          setMenuToDelete(menu);
          setDeleteDialogOpen(true);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteMenuDialog
        menu={menuToDelete}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </div>
  );
}
